import { isDeliveries } from '../../helpers/appType';
import { ConversationAttachmentType } from '../../types/conversation.types';
import { presignAttachment } from './conversationTransport';

/**
 * Client-side half of the direct-to-Spaces upload.
 *
 * The file never goes through the vGrubs API - it is PUT straight to storage
 * with a presigned URL. That has two consequences everything here is built
 * around:
 *
 *   1. The request must carry EXACTLY the headers the backend signed, and
 *      nothing else. In particular it must not go through the shared axios
 *      instance, which adds `Authorization`, `app-version` and a request-side
 *      `Access-Control-Allow-Origin: '*'` to every call. The last one both
 *      breaks the signature and triggers a preflight Spaces rejects.
 *   2. Progress needs `XMLHttpRequest` - `fetch` cannot report upload progress.
 */

export const MAX_ATTACHMENTS_PER_MESSAGE = 10;

const MB = 1024 * 1024;

type AttachmentLimits = Record<
  ConversationAttachmentType,
  { mimes: string[]; maxSize: number; label: string }
>;

/** Mirrors the backend's per-type allowlist; validated before presigning so
 * the user isn't made to wait for an upload that was always going to fail. */
const CONVERSATION_LIMITS: AttachmentLimits = {
  image: {
    mimes: [
      'image/jpeg',
      'image/png',
      'image/webp',
      'image/gif',
      'image/heic',
      'image/heif',
    ],
    maxSize: 20 * MB,
    label: 'Images',
  },
  video: {
    mimes: ['video/mp4', 'video/quicktime', 'video/webm'],
    maxSize: 200 * MB,
    label: 'Videos',
  },
  audio: {
    mimes: ['audio/mpeg', 'audio/mp4', 'audio/aac', 'audio/ogg'],
    maxSize: 25 * MB,
    label: 'Audio',
  },
  file: {
    mimes: ['application/pdf'],
    maxSize: 25 * MB,
    label: 'PDF',
  },
};

/**
 * The vDrive driver chat (the deliveries console) is a different backend
 * with the same shape: several files per message, its own caps.
 */
const DRIVER_CHAT_LIMITS: AttachmentLimits = {
  image: {
    mimes: [
      'image/jpeg',
      'image/png',
      'image/webp',
      'image/gif',
      'image/heic',
      'image/heif',
    ],
    maxSize: 100 * MB,
    label: 'Images',
  },
  video: {
    mimes: ['video/mp4', 'video/quicktime', 'video/webm'],
    maxSize: 200 * MB,
    label: 'Videos',
  },
  audio: {
    mimes: [
      'audio/mp4',
      'audio/x-m4a',
      'audio/aac',
      'audio/mpeg',
      'audio/ogg',
      'audio/webm',
      'audio/wav',
    ],
    maxSize: 10 * MB,
    label: 'Audio',
  },
  file: { mimes: ['application/pdf'], maxSize: 25 * MB, label: 'PDF' },
};

export const CHAT_ATTACHMENT_LIMITS: AttachmentLimits = isDeliveries
  ? DRIVER_CHAT_LIMITS
  : CONVERSATION_LIMITS;

export const ACCEPTED_MIMES = Object.values(CHAT_ATTACHMENT_LIMITS)
  .flatMap((limit) => limit.mimes)
  .join(',');

/** Normalises `audio/ogg; codecs=opus` down to the bare type. */
const baseMime = (mime: string) => mime.split(';')[0].trim().toLowerCase();

export const attachmentTypeFor = (
  mime: string
): ConversationAttachmentType | null => {
  const base = baseMime(mime);
  const entry = (
    Object.entries(CHAT_ATTACHMENT_LIMITS) as [
      ConversationAttachmentType,
      (typeof CHAT_ATTACHMENT_LIMITS)[ConversationAttachmentType]
    ][]
  ).find(([, limit]) => limit.mimes.includes(base));
  return entry ? entry[0] : null;
};

/** Returns an error message, or null when the file is acceptable. */
export const validateAttachment = (file: File): string | null => {
  const type = attachmentTypeFor(file.type);
  if (!type) return `${file.name}: this file type isn't supported.`;

  const { maxSize } = CHAT_ATTACHMENT_LIMITS[type];
  if (file.size > maxSize) {
    return `${file.name} is too large (max ${Math.round(maxSize / MB)} MB).`;
  }
  return null;
};

/* -------------------------------------------------------------------------- */
/* Metadata probing                                                           */
/* -------------------------------------------------------------------------- */

export type MediaMetadata = {
  width?: number;
  height?: number;
  durationMs?: number;
};

/**
 * heic/heif are accepted by the backend but Chrome's canvas can't decode them,
 * so probing quietly returns nothing rather than failing the upload - the
 * dimensions are optional in the presign payload.
 */
export const probeImage = (file: File): Promise<MediaMetadata> =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      resolve({ width: image.naturalWidth, height: image.naturalHeight });
      URL.revokeObjectURL(url);
    };
    image.onerror = () => {
      resolve({});
      URL.revokeObjectURL(url);
    };
    image.src = url;
  });

export const probeVideo = (
  file: File
): Promise<MediaMetadata & { posterBlob?: Blob }> =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    // Required on iOS, which otherwise refuses to decode frames off-screen.
    video.playsInline = true;

    const done = (result: MediaMetadata & { posterBlob?: Blob }) => {
      URL.revokeObjectURL(url);
      resolve(result);
    };

    video.onloadedmetadata = () => {
      const meta: MediaMetadata = {
        width: video.videoWidth,
        height: video.videoHeight,
        durationMs: Math.round(video.duration * 1000),
      };

      // The backend does not generate posters, so capture one here. Frame 0 is
      // usually black, hence the small seek.
      video.onseeked = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          const context = canvas.getContext('2d');
          if (!context) return done(meta);
          context.drawImage(video, 0, 0, canvas.width, canvas.height);
          canvas.toBlob(
            (blob) => done({ ...meta, posterBlob: blob ?? undefined }),
            'image/jpeg',
            0.8
          );
        } catch {
          // Cross-origin or unsupported codec - ship it without a poster.
          done(meta);
        }
      };
      video.onerror = () => done(meta);
      video.currentTime = Math.min(0.1, video.duration || 0.1);
    };

    video.onerror = () => done({});
    video.src = url;
  });

export const probeAudioDuration = (blob: Blob): Promise<number | undefined> =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(blob);
    const audio = document.createElement('audio');
    audio.preload = 'metadata';
    audio.onloadedmetadata = () => {
      const duration = Number.isFinite(audio.duration)
        ? Math.round(audio.duration * 1000)
        : undefined;
      URL.revokeObjectURL(url);
      resolve(duration);
    };
    audio.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(undefined);
    };
    audio.src = url;
  });

/* -------------------------------------------------------------------------- */
/* Upload                                                                     */
/* -------------------------------------------------------------------------- */

export class UploadError extends Error {
  constructor(
    public status: number,
    /**
     * True when the request never produced a readable HTTP response at all.
     * XHR reports this as `status: 0`, which on its own tells nobody anything.
     * In practice it means the device is offline, or - far more likely for a
     * direct-to-Spaces PUT - the bucket has no CORS rule covering this origin,
     * so the browser refuses to expose the response to us.
     */
    public network = false
  ) {
    super(
      network
        ? "Couldn't reach file storage. If this keeps happening, the storage bucket is missing a CORS rule for this site."
        : `Upload failed with status ${status}.`
    );
    this.name = 'UploadError';
  }
}

/**
 * PUT a blob to a presigned URL.
 *
 * `headers` must be passed through verbatim from the presign response - adding
 * or changing even one header invalidates the signature.
 */
export const putToPresignedUrl = ({
  url,
  headers,
  body,
  onProgress,
  signal,
}: {
  url: string;
  headers: Record<string, string>;
  body: Blob;
  onProgress?: (fraction: number) => void;
  signal?: AbortSignal;
}) =>
  new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);

    Object.entries(headers).forEach(([key, value]) =>
      xhr.setRequestHeader(key, value)
    );

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(event.loaded / event.total);
      }
    };

    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new UploadError(xhr.status));

    // A cross-origin PUT carrying `x-amz-acl` can never be a "simple" request,
    // so the browser always preflights it. Without a matching CORS rule on the
    // bucket the preflight fails and this fires with `status: 0` - there is no
    // client-side workaround, the bucket has to allow the origin.
    xhr.onerror = () => reject(new UploadError(0, true));
    xhr.onabort = () => reject(new UploadError(0, true));

    signal?.addEventListener('abort', () => xhr.abort(), { once: true });

    xhr.send(body);
  });

/**
 * The whole pipeline for one file: probe its metadata, presign, PUT to Spaces
 * and hand back the `attachmentId` to send with a message.
 *
 * Shared by the staged-attachments hook and the image editor's send, so the
 * probe rules and the re-presign retry exist exactly once.
 */
export const uploadAttachment = async (
  conversationId: number | string,
  file: File,
  type: ConversationAttachmentType,
  {
    signal,
    onProgress,
  }: {
    signal?: AbortSignal;
    onProgress?: (fraction: number) => void;
  } = {}
): Promise<number> => {
  // 1. Probe. Optional everywhere - heic can't be decoded by canvas, and a
  //    failed probe just means the presign carries less metadata.
  let width: number | undefined;
  let height: number | undefined;
  let durationMs: number | undefined;
  let posterBlob: Blob | undefined;

  if (type === 'image') {
    ({ width, height } = await probeImage(file));
  } else if (type === 'video') {
    ({ width, height, durationMs, posterBlob } = await probeVideo(file));
  } else if (type === 'audio') {
    durationMs = await probeAudioDuration(file);
  }

  // 2. Presign. Asking for a thumbnail URL only when we actually produced a
  //    poster keeps the response honest.
  const presign = async () => {
    const { data } = await presignAttachment(conversationId, {
      mime: file.type,
      size: file.size,
      name: file.name,
      ...(width ? { width } : {}),
      ...(height ? { height } : {}),
      ...(durationMs ? { durationMs } : {}),
      ...(posterBlob ? { thumbnailMime: 'image/jpeg' } : {}),
    });
    return data.data;
  };

  let signed = await presign();

  // 3. Upload with the signed headers, exactly as returned.
  const put = (target = signed) =>
    putToPresignedUrl({
      url: target.uploadUrl,
      headers: target.headers,
      body: file,
      signal,
      onProgress,
    });

  try {
    await put();
  } catch (error) {
    // A big video on a slow uplink can outlive the 15 minute URL. One
    // re-presign is enough; a second failure is a real failure.
    if (error instanceof UploadError && error.status === 403) {
      signed = await presign();
      await put(signed);
    } else {
      throw error;
    }
  }

  if (posterBlob && signed.thumbnailUploadUrl) {
    await putToPresignedUrl({
      url: signed.thumbnailUploadUrl,
      // vDrive signs the poster on its own; vGrubs reuses the file's.
      headers: signed.thumbnailHeaders ?? {
        ...signed.headers,
        'Content-Type': 'image/jpeg',
      },
      body: posterBlob,
      signal,
    });
  }

  return signed.attachmentId;
};
